import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { theme } from '../../../../core/design-system/theme'
import { useBookingContext, BookingStepShell } from '../BookingFlowView'

const prefersReducedMotion =
  typeof window !== 'undefined'
    ? window.matchMedia('(prefers-reduced-motion: reduce)').matches
    : false

function Spinner() {
  return (
    <>
      {!prefersReducedMotion && (
        <style>{`@keyframes nonna-spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
      )}
      <svg
        width="20"
        height="20"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.5"
        strokeLinecap="round"
        role="img"
        aria-label="Loading"
        style={
          prefersReducedMotion
            ? { display: 'inline-block', verticalAlign: 'middle' }
            : {
                display: 'inline-block',
                verticalAlign: 'middle',
                animation: 'nonna-spin 0.8s linear infinite',
              }
        }
      >
        <path d="M12 2a10 10 0 0 1 10 10" />
      </svg>
    </>
  )
}

const PAYMENT_METHODS = [
  {
    id: 'card',
    label: 'Credit / Debit card',
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <rect x="1" y="4" width="22" height="16" rx="2" />
        <line x1="1" y1="10" x2="23" y2="10" />
      </svg>
    ),
  },
  {
    id: 'apple_pay',
    label: 'Apple Pay',
    icon: (
      <svg
        width="22"
        height="22"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        aria-hidden="true"
      >
        <rect x="3" y="4" width="18" height="16" rx="3" />
        <path d="M7 12h10M12 8v8" />
      </svg>
    ),
  },
]

export default function BookingStep6View() {
  const { viewModel, repository, session } = useBookingContext()
  const navigate = useNavigate()

  const [paymentMethod, setPaymentMethod] = useState('card')
  const isSubmitting = viewModel.submissionStatus === 'submitting'
  const error = viewModel.error

  async function handleConfirm() {
    if (isSubmitting) return
    const booking = await viewModel.confirm(repository, session.user.id)
    if (booking) {
      navigate(`/booking/${booking.id}`)
    }
  }

  return (
    <BookingStepShell
      step={6}
      title="How would you like to pay?"
      onBack={() => navigate(-1)}
      onContinue={handleConfirm}
      continueLabel={isSubmitting ? <Spinner /> : 'Confirm & Pay'}
      continueDisabled={isSubmitting}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {PAYMENT_METHODS.map((method) => {
          const isSelected = paymentMethod === method.id
          return (
            <button
              key={method.id}
              onClick={() => setPaymentMethod(method.id)}
              aria-pressed={isSelected}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 14,
                background: theme.colors.card,
                border: `1.5px solid ${isSelected ? theme.colors.terracotta : theme.colors.border}`,
                borderRadius: theme.layout.cardRadius,
                padding: '16px 18px',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
                transition: prefersReducedMotion ? 'none' : 'border-color 0.15s ease',
              }}
            >
              <span
                style={{
                  color: isSelected ? theme.colors.terracotta : theme.colors.textSecondary,
                  lineHeight: 0,
                }}
              >
                {method.icon}
              </span>
              <span
                style={{
                  flex: 1,
                  fontSize: 15,
                  fontWeight: isSelected ? 700 : 500,
                  color: theme.colors.textPrimary,
                  fontFamily: 'var(--font-body)',
                }}
              >
                {method.label}
              </span>
              {isSelected && (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  aria-hidden="true"
                >
                  <circle cx="12" cy="12" r="10" fill={theme.colors.terracotta} />
                  <path
                    d="M8 12.5l2.5 2.5 5-5"
                    stroke="#fff"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              )}
            </button>
          )
        })}

        {error && (
          <div
            role="alert"
            style={{
              background: theme.colors.errorBg,
              border: `1px solid ${theme.colors.errorText}`,
              borderRadius: theme.layout.inputRadius,
              padding: '12px 14px',
              color: theme.colors.errorText,
              fontSize: 14,
              fontFamily: 'var(--font-body)',
              marginTop: 4,
            }}
          >
            {error}
          </div>
        )}
      </div>
    </BookingStepShell>
  )
}
